import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import Storage from 'grab-storage';
import { getUserCountryCode } from 'helper/user';
import { authorizedFetch } from 'actions/fetch-helper';
import geoEndpoints from 'actions/geo-endpoints';
import {
  fetchAddressSearch,
  fetchLatlngReverse,
  getCityRefCoordinates,
} from './address-search-helpers';
import { isToggledCountry } from './geo-helper';
import { STORAGE_USER_COUNTRY_CODE } from '../constants/app-constants';

const { v3SearchAddressEndpoint, v3ReverseGeoEndpoint } = geoEndpoints;

// eslint-disable-next-line no-useless-escape
const latlngRegex = new RegExp(/^(\-?\d+(\.\d+)?),\s*(\-?\d+(\.\d+)?)$/);

export const isLatLngString = str => latlngRegex.test(str);

export const fetchAddressV3 = ({
  keyword,
  isPax,
  company = {},
  city,
  pickupOrDropoff,
  latlng,
}) => {
  const countryCode = city.countryCode;
  const isToggle = isToggledCountry(countryCode) || false;
  const searchParams = {
    keyword,
    latlng,
    isToggle,
    pickupOrDropoff,
  };
  const companyID = isPax ? null : _get(company, 'id');
  return authorizedFetch(
    v3SearchAddressEndpoint(searchParams),
    companyID,
    true,
  );
};

export const reverseLatLngInTheCityV3 = ({
  keyword,
  isPax,
  company = {},
  city,
  pickupOrDropoff,
}) => {
  const countryCode = _get(city, 'countryCode');
  const isToggle = isToggledCountry(countryCode) || false;
  const companyID = isPax ? null : _get(company, 'id');
  const latlng = keyword.split(',');
  return authorizedFetch(
    v3ReverseGeoEndpoint({
      latitude: latlng[0].trim(),
      longitude: latlng[1].trim(),
      pickupOrDropoff,
      isToggle,
    }),
    companyID,
    true,
  ).then(origResp => {
    // check city ID and make result an array
    const result =
      _get(origResp, 'response.result.address.cityId') === _get(city, 'cityID')
        ? [origResp.response.result]
        : [];
    _set(origResp, 'response.result', result);
    return origResp;
  });
};

export const fetchAddressV2 = ({
  keyword,
  city,
  isPax,
  company = {},
  isReverse = false,
  additionalData = undefined,
}) => {
  const countryCode =
    additionalData && additionalData.countryCode
      ? additionalData.countryCode
      : isPax
        ? Storage.getItem(STORAGE_USER_COUNTRY_CODE)
        : company.countryCode;
  const isToggle =
    isToggledCountry(countryCode) ||
    (additionalData && !!additionalData.isToggle) ||
    false;
  if (isReverse) {
    const latlng = keyword.split(',');
    const reverseParams = {
      lat: latlng[0].trim(),
      lng: latlng[1].trim(),
      isPax,
    };
    if (!isPax) {
      reverseParams.companyID = company.id;
    }
    return fetchLatlngReverse(reverseParams);
  }

  const searchParams = {
    keyword,
    latlng:
      additionalData && additionalData.lat && additionalData.lng
        ? {
            lat: additionalData && additionalData.lat,
            lng: additionalData && additionalData.lng,
          }
        : city &&
          city.coordinates &&
          city.coordinates.latitude &&
          city.coordinates.longitude
          ? {
              lat: city.coordinates.latitude,
              lng: city.coordinates.longitude,
            }
          : {},
    countryCode,
    isToggle,
    isPax,
  };

  if (!isPax) {
    searchParams.companyID = company.id;
  }
  return fetchAddressSearch(searchParams);
};

export const makeSearchAddressFunc = ({
  isPax,
  city,
  company,
  isPickUp,
}) => keyword => {
  const pickupOrDropoff = isPickUp ? 'pickup' : 'dropoff';
  if (isLatLngString(keyword)) {
    return reverseLatLngInTheCityV3({
      keyword,
      isPax,
      company,
      city,
      pickupOrDropoff,
    });
  }

  const latlng = getCityRefCoordinates(city, true) || {};
  return fetchAddressV3({
    keyword,
    isPax,
    company,
    city,
    pickupOrDropoff,
    latlng,
  });
};

export const fetchAddress = async (
  keyword,
  city,
  company,
  additionalData = undefined,
) => {
  const isToggle =
    isToggledCountry(_get(city, 'countryCode')) ||
    (additionalData && !!additionalData.isToggle) ||
    false;
  let data;
  let searchParams;
  let reverseParams;
  const isPax = isPaxUser();
  if (latlngRegex.test(keyword)) {
    const latlng = keyword.split(',');
    reverseParams = {
      lat: latlng[0].trim(),
      lng: latlng[1].trim(),
      isPax,
      isToggle,
    };
    if (!isPax) {
      reverseParams.companyID = company.id;
    }
    data = await fetchLatlngReverse(reverseParams);
    return { data, isReverse: true };
  }

  searchParams = {
    keyword,
    latlng:
      additionalData && additionalData.lat && additionalData.lng
        ? {
            lat: additionalData && additionalData.lat,
            lng: additionalData && additionalData.lng,
          }
        : !_isEmpty(city)
          ? {
              lat: city.coordinates.latitude,
              lng: city.coordinates.longitude,
            }
          : {},
    countryCode:
      additionalData && additionalData.countryCode
        ? additionalData.countryCode
        : getUserCountryCode(company),
    isToggle,
    isPax,
  };

  if (!isPax) searchParams.companyID = company.id;
  data = await fetchAddressSearch(searchParams);
  return { data, isReverse: false };
};

export const reverseOrderPOIs = async (order, companyID, isPax) => {
  const steps = _get(order, 'steps', []);

  if (_isEmpty(steps)) return [];

  const latlngs = steps.reduce((acc, step) => {
    const latlng = _get(step, 'location.coordinates', {});

    return isNaN(latlng.latitude) ? acc : acc.concat(latlng);
  }, []);

  const pois = await Promise.all(
    latlngs.map(
      latlng =>
        new Promise(async resolve => {
          const reversePOI = await fetchLatlngReverse({
            lat: latlng.latitude,
            lng: latlng.longitude,
            companyID,
            isPax,
          });
          resolve({ ..._get(reversePOI, 'resp.result') });
        }),
    ),
  );

  return pois;
};

export const buildResolvePOIsPayload = (resolvingRequests, city) => {
  const countryCode = _get(city, 'countryCode');
  const isToggle = isToggledCountry(countryCode) || false;
  const cityRefCoordinates = getCityRefCoordinates(city, true) || {};

  const resolvingPOIs = resolvingRequests.map(
    ({ key, keyword, pickupOrDropoff, adminAreaPath }) => {
      const resolvingPOI = {
        key,
        pickupOrDropoff,
      };
      if (isLatLngString(keyword)) {
        const latlng = keyword.split(',');
        resolvingPOI.latitude = Number(latlng[0].trim());
        resolvingPOI.longitude = Number(latlng[1].trim());
      } else {
        resolvingPOI.latitude = cityRefCoordinates.latitude;
        resolvingPOI.longitude = cityRefCoordinates.longitude;
        resolvingPOI.keyword = keyword;
        resolvingPOI.adminAreaPath = adminAreaPath;
      }
      return resolvingPOI;
    },
  );

  return {
    isToggle,
    resolvingPOIs,
  };
};

export default {};
